import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  inject,
  Output,
  EventEmitter
} from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { compile, parse } from 'expression-eval';
import { MessageService } from 'primeng/api';
import { ApiConstants } from 'src/app/base/api-constants';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { nodeType } from 'src/app/pages/designs/data-model/data-model.component';
import { getFieldsForTable } from 'src/app/STORE/actions/services.actions';
import { tableFieldsForFormula } from 'src/app/STORE/selectors/services.selector';
import { TranslateService } from '@ngx-translate/core';
import { v4 as uuidv4 } from 'uuid';
import { UtilService } from 'src/app/services/util.service';
@Component({
  selector: 'vb-formula-builder',
  templateUrl: './formula-builder.component.html',
  styleUrls: ['./formula-builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormulaBuilderComponent),
      multi: true
    }
  ],
  standalone: false //NOSONAR
})
export class FormulaBuilderComponent implements OnInit, OnDestroy {
  private confirmationDialogService = inject(ConfirmationDialogService);
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);
  private store = inject(Store);
  private baseService = inject(HttpBaseService);
  private messageService = inject(MessageService);
  private translate = inject(TranslateService);
  private util = inject(UtilService);
  @Output() errorStatus = new EventEmitter<'error' | 'warning' | 'none'>();
  currentErrorStatus: 'error' | 'warning' | 'none' = 'none';
  expressionForm: FormGroup = new FormGroup({
    expression: new FormControl('', [Validators.required])
  });

  @Input() element: any;
  @Input() currentNode: any = null;
  @Input() disableField = false;
  @Input() fieldIdMap: any = null;
  @Input() dataModelOrders: any = null;
  private modelValue: any;
  private onChange: (m: any) => void;
  private onTouched: (m: any) => void;
  disabled: boolean;
  allFields: any;
  allFields$: any = null;
  lookupFields$: any = null;
  testOutput: any = null;
  allLookupFields: any = [];

  currentFormula = '';
  currentParsedFormula = '';
  currentActualFormula = '';
  operators: any[] = [
    {
      label: ' + ',
      name: 'add'
    },
    {
      label: ' - ',
      name: 'sub'
    },
    {
      label: ' / ',
      name: 'div'
    },
    {
      label: ' * ',
      name: 'mul'
    },
    {
      label: ' ( ',
      name: 'brack_open'
    },
    {
      label: ' ) ',
      name: 'brack_close'
    }
  ];
  selectedField: any;
  selectedFields: any[] = [];
  parsedFormula: any = null;
  parsedFunction: any = null;
  formulaForm: FormGroup = new FormGroup({});
  showForm = false;
  formulaArray: any[] = [];
  savedValue: any = null;
  showFbuilder = false;
  selectedLookupField: any;
  allFieldsLoading = false;
  lookupFieldsLoading = false;
  numberFld: any = '';
  commaFormula = false;
  errorMsg: string = '';
  allFieldsBeforeFiltering: any = null;

  get model() {
    return this.modelValue;
  }

  writeValue(value: any): void {
    this.savedValue = value;
    this.setConfiguredFormula(value);
    this.modelValue = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  set(value: any) {
    this.modelValue = value;
    this.onChange(this.modelValue);
  }

  setDisabledState(isDisabled: boolean) {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
    this.disabled = isDisabled;
  }

  constructDisplayFormula(fromExisting?: boolean) {
    this.currentActualFormula = '';
    this.formulaArray.map((f: any) => {
      if (fromExisting && f.type === 'field' && !this.selectedFields.find((fld) => fld.fieldId === f.fieldId)) {
        let _fieldName = this.fieldIdMap[f.fieldId]?.includes('.')
          ? this.fieldIdMap[f.fieldId].split('.')[1]
          : this.fieldIdMap[f.fieldId];
        _fieldName = _fieldName?.replace(/ /g, '');
        f.formControlName = _fieldName?.toLowerCase();
        this.selectedFields.push(f);
      }
      if (f.type === 'field') {
        this.currentActualFormula += `${this.fieldIdMap[f.fieldId]}`;
      } else {
        this.currentActualFormula += f.fieldId;
      }
    });
    this.formulaForm = new FormGroup(this.constructTestForm());
    this.validateFormula();
  }

  handleFieldSelection(e: any) {
    if (e.value) {
      if (e.value.f_type.toLowerCase() !== 'lookup') {
        const field: any = {
          type: 'field',
          fieldId: e.value.sid,
          sid: uuidv4()
        };
        this.formulaArray.push(field);
        if (!this.selectedFields.find((fld) => fld.fieldId === field.fieldId)) {
          let _fieldName = this.fieldIdMap[field.fieldId].includes('.')
            ? this.fieldIdMap[field.fieldId].split('.')[1]
            : this.fieldIdMap[field.fieldId];
          _fieldName = _fieldName.replace(/ /g, '');
          field.formControlName = _fieldName.toLowerCase();
          this.selectedFields.push(field);

          if (this.showForm) {
            this.formulaForm.addControl(_fieldName.toLowerCase(), new FormControl());
          }
        }
        this.constructDisplayFormula();
      } else {
        this.allLookupFields = [];
        this.getLookupFields(e.value.f_lookupTo);
      }
    }
    setTimeout(() => {
      this.selectedField = '';
    }, 0);
  }

  getLookupFields(tableId: string) {
    this.lookupFieldsLoading = true;
    this.lookupFields$ = this.baseService.get(ApiConstants.getFieldInfo, { tableId }).subscribe((data: any) => {
      this.allLookupFields = data.filter((f: any) => {
        return ['number'].includes(f.f_type.toLowerCase());
      });
      this.lookupFieldsLoading = false;
    });
  }

  handleLookupFieldSelection(e: any) {
    if (e.value) {
      const field = {
        type: 'field',
        fieldId: e.value.sid,
        sid: uuidv4()
      };
      this.formulaArray.push(field);
      this.selectedFields.push(field);
      this.constructDisplayFormula();
    }
  }

  handleOperatorClick(e: any, operator?: any) {
    const oper = {
      sid: uuidv4(),
      type: 'operator',
      fieldId: operator.label
    };
    this.formulaArray.push(oper);
    this.constructDisplayFormula();
  }

  handleNumberIp(e: any) {
    const val = this.numberFld;
    if (val) {
      const num = {
        sid: uuidv4(),
        type: 'number',
        fieldId: Number(this.numberFld)
      };
      this.formulaArray.push(num);
      this.constructDisplayFormula();
      this.numberFld = null;
    }
  }

  getFields(id) {
    if (id) {
      this.allFieldsLoading = true;
      this.store.dispatch(
        getFieldsForTable({
          fstAPiConstants: ApiConstants.getFieldInfo,
          params: { tableId: id }
        })
      );

      this.allFields$ = this.store.select(tableFieldsForFormula).subscribe((fields: any) => {
        if (fields) {
          this.allFieldsBeforeFiltering = fields;
          this.allFields = fields.filter((f: any) => {
            if (
              this.currentNode.f_type.toLowerCase() === 'currency' ||
              this.currentNode.f_type.toLowerCase() === 'double'
            ) {
              return (
                ['number', 'double', 'currency'].includes(f.f_type.toLowerCase()) && f.sid !== this.currentNode.sid
              );
            }
            return ['number'].includes(f.f_type.toLowerCase()) && f.sid !== this.currentNode.sid;
          });
          this.allFieldsLoading = false;
          this.validateFormula();
        }
      });
    }
  }

  isNumberKey(evt) {
    const charCode = evt.which ? evt.which : evt.key;
    if (this.currentNode.f_type.toLowerCase() === 'number') {
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        return false;
      }
    } else {
      if (charCode !== 46 && charCode !== 44 && charCode > 31 && (charCode < 48 || charCode > 57)) {
        return false;
      }
      if (
        this.numberFld &&
        (this.numberFld.includes('.') || this.numberFld.includes(',')) &&
        (charCode === 44 || charCode === 46)
      ) {
        return false;
      }
    }
    return true;
  }

  showPopup: boolean = false;
  isWarning: boolean = false;

  toggleWarningPopup() {
    this.showPopup = !this.showPopup;
  }
  validateFieldsUsedInFormula(): boolean {
    const allFieldsSids = new Set(this.allFieldsBeforeFiltering.map((f) => f.sid));
    const validFields = new Set(this.allFields.map((f) => f.sid));
    const invalidFieldType = this.formulaArray
      .filter((f) => !validFields.has(f.fieldId) && allFieldsSids.has(f.fieldId) && f.type == 'field')
      .map((f) => f.fieldId);
    if (invalidFieldType.length) {
      this.util.handleDeletedIds(invalidFieldType, false, '', false, 'formulaBuilderFieldTypeChange', (message) => {
        this.setErrorStatus('warning', message);
      });
      return false;
    }

    const deletedFields = this.formulaArray
      .filter((f) => !allFieldsSids.has(f.fieldId) && f.type == 'field')
      .map((f) => f.fieldId);
    if (deletedFields.length) {
      this.util.handleDeletedIds(deletedFields, false, '', false, 'formulaBuilderFieldDeleted', (message) => {
        this.setErrorStatus('error', message);
      });
      return false;
    }
    return true;
  }

  private isParenthesis(value: string | null): boolean {
    const trimmed = value?.trim();
    return trimmed === '(' || trimmed === ')';
  }

  private hasConsecutiveNonOperators(lastType: string | null, currentType: string): boolean {
    return !!lastType && !(lastType === 'operator' || currentType === 'operator');
  }

  private hasInvalidConsecutiveOperators(lastType: string | null, currentType: string, currentFieldId: string, lastLabel: string | null): boolean {
    return lastType === 'operator' &&
      currentType === 'operator' &&
      !this.isParenthesis(currentFieldId) &&
      !this.isParenthesis(lastLabel);
  }

  private hasInvalidParenthesisPlacement(lastType: string | null, lastLabel: string | null, currentType: string, currentFieldId: string): boolean {
    if (!lastType || !lastLabel) {
      return false;
    }
    const isOpeningParen = currentType === 'operator' && currentFieldId.trim() === '(';
    const isAfterClosingParen = lastLabel === ')';

    if (!isOpeningParen && !isAfterClosingParen) {
      return false;
    }
    const openParenWithoutOperator = isOpeningParen && lastType !== 'operator';
    const closingParenWithoutOperator = isAfterClosingParen && currentType !== 'operator';

    return openParenWithoutOperator || closingParenWithoutOperator;
  }

  private isInvalidFirstElement(element: any): boolean {
    if (this.formulaArray.length === 1) {
      return true;
    }
    return element.type === 'operator' && !this.isParenthesis(element.fieldId);
  }

  private hasConsecutiveFieldsOrNumbers(lastType: string | null, currentType: string): boolean {
    return lastType === currentType && (currentType === 'field' || currentType === 'number');
  }

  private validateFormulaSequence(): boolean {
    let lastType: string | null = null;
    let label: string | null = null;

    for (const f of this.formulaArray) {
      if (!this.isValidFormulaElement(f, lastType, label)) {
        return false;
      }
      lastType = this.updateLastType(lastType, f);
      label = f.type !== 'number' ? f.fieldId.trim() : f.fieldId;
    }
    return true;
  }

  private isValidFormulaElement(f: any, lastType: string | null, label: string | null): boolean {
    // Check consecutive non-operators or invalid consecutive operators
    if (this.hasConsecutiveNonOperators(lastType, f.type) ||
        this.hasInvalidConsecutiveOperators(lastType, f.type, f.fieldId, label)) {
      return false;
    }
    // Check parenthesis placement
    if (this.hasInvalidParenthesisPlacement(lastType, label, f.type, f.fieldId)) {
      return false;
    }
    // Check first element validity
    if (!lastType && this.isInvalidFirstElement(f)) {
      return false;
    }
    // Check consecutive fields or numbers
    if (this.hasConsecutiveFieldsOrNumbers(lastType, f.type)) {
      return false;
    }
    return true;
  }

  private updateLastType(lastType: string | null, f: any): string {
    if (!lastType || lastType !== f.type) {
      return f.type;
    }
    return lastType;
  }

  private hasArithmeticOperator(): boolean {
    return this.formulaArray.some(
      (f) => f.type === 'operator' && !this.isParenthesis(f.fieldId)
    );
  }

  validateFormula() {
    try {
      if (!this.formulaArray.length) {
        this.setErrorStatus('none', '');
        return true;
      }

      if (this.currentActualFormula.includes(',')) {
        this.commaFormula = true;
      }

      if (!this.validateFieldsUsedInFormula()) {
        return false;
      }

      const formula = this.currentActualFormula.replace(',', '.').toLowerCase().replace(/\s/g, '');
      this.parsedFormula = parse(formula);
      this.parsedFunction = compile(formula);

      if (!this.validateFormulaSequence() || !this.hasArithmeticOperator()) {
        this.setErrorStatus('error', this.translate.instant('INVALID_FORMULA'));
        return false;
      }

      this.setErrorStatus('none', '');
      return true;
    } catch (error) {
      this.setErrorStatus('error', this.translate.instant('INVALID_FORMULA'));
      return false;
    }
  }

  setErrorStatus(status: 'error' | 'warning' | 'none', msg: string) {
    this.errorMsg = msg;
    this.currentErrorStatus = status;
    if (status === 'error') {
      this.isWarning = false;
      this.errorStatus.emit('error');
    }
    if (status === 'warning') {
      this.isWarning = true;
      this.errorStatus.emit('warning');
    }
    if (status === 'none') {
      this.isWarning = false;
      this.errorStatus.emit('none');
    }
  }

  constructTestForm() {
    const formControls = {};
    this.selectedFields.map((field: any) => {
      let _fieldName = this.fieldIdMap[field.fieldId]?.includes('.')
        ? this.fieldIdMap[field.fieldId].split('.')[1]
        : this.fieldIdMap[field.fieldId];
      _fieldName = _fieldName?.replace(/ /g, '');
      formControls[_fieldName?.toLowerCase()] = new FormControl();
    });
    return formControls;
  }

  testFormula() {
    this.validateFormula();
    if (!this.currentActualFormula.length) {
      return;
    }
    let formControls;
    this.testOutput = null;
    if (this.showForm) {
      const selFieldsCopy = [...this.selectedFields];
      this.selectedFields = [];
      formControls = {};
      setTimeout(() => {
        this.selectedFields = selFieldsCopy;
        formControls = this.constructTestForm();
        this.formulaForm = new FormGroup(formControls);
        this.showForm = true;
      }, 100);
    } else {
      formControls = this.constructTestForm();
      this.formulaForm = new FormGroup(formControls);
      this.showForm = true;
    }
  }

  calculateOutput() {
    if (this.validateFormula()) {
      const formVal = this.formulaForm.value;
      const fieldVal = {};
      this.selectedFields.map((field: any) => {
        let _fieldName = this.fieldIdMap[field.fieldId].includes('.')
          ? this.fieldIdMap[field.fieldId].split('.')[1]
          : this.fieldIdMap[field.fieldId];
        _fieldName = _fieldName.replace(/ /g, '');
        fieldVal[_fieldName.toLowerCase()] = formVal[_fieldName.toLowerCase()];
      });
      try {
        if (this.currentActualFormula.includes(',')) {
          this.commaFormula = true;
        }
        const formula = this.currentActualFormula.replace(',', '.').toLowerCase();
        this.parsedFunction = compile(formula);
      } catch (e) {
        console.error(e);
      }
      const output = this.parsedFunction(fieldVal);
      if (this.commaFormula) {
        this.testOutput = output.toString().replace('.', ',');
      } else {
        this.testOutput = output;
      }
    }
  }

  clearForm() {
    this.formulaForm.reset();
    this.formulaForm = new FormGroup(this.constructTestForm());
    this.testOutput = null;
  }

  removeItem(e: any, item) {
    this.formulaArray = this.formulaArray.filter((f: any) => f.sid !== item.sid);
    if (item.type === 'field') {
      this.selectedFields = this.selectedFields.filter((f: any) => f.sid !== item.sid);
      let _fieldName = this.fieldIdMap[item.fieldId].includes('.')
        ? this.fieldIdMap[item.fieldId].split('.')[1]
        : this.fieldIdMap[item.fieldId];
      _fieldName = _fieldName.replace(/ /g, '');
      this.formulaForm.removeControl(_fieldName.toLowerCase());
    }
    this.constructDisplayFormula();
  }

  private hasCircularReference(formula: any[], fb: string): boolean {
    const usesCurrentField = formula.find((obj) => obj.type === 'field' && obj.fieldId === this.currentNode.sid);
    const formulaUsesField = this.formulaArray.find((fld) => fld.fieldId === fb);
    return !!(usesCurrentField && formulaUsesField);
  }

  private checkFormulaForCircularRef(tblfbs: any): boolean {
    for (const fb in tblfbs) {
      if (!tblfbs[fb]) {
        continue;
      }
      const formula = JSON.parse(tblfbs[fb]);
      if (formula.length && this.hasCircularReference(formula, fb)) {
        this.errorMsg = `The field ${this.fieldIdMap[this.currentNode.sid]} is used in the formula of ${this.fieldIdMap[fb]}. Hence ${this.fieldIdMap[fb]} cannot be used here.`;
        return true;
      }
    }
    return false;
  }

  compareFbuilder(node) {
    try {
      if (!node.data.t_formulas) {
        return false;
      }
      const tblfbs = JSON.parse(node.data.t_formulas);
      return this.checkFormulaForCircularRef(tblfbs);
    } catch (e) {
      /* Empty */
      return false;
    }
  }

  isCirRef() {
    let isRef;
    this.dataModelOrders.forEach((node) => {
      if (!node.title) {
        return;
      }
      if (node.type === nodeType.TABLE && node.data?.sid === this.currentNode.f_tableId) {
        isRef = this.compareFbuilder(node);
        return;
      } else if (node.type === nodeType.GROUP) {
        if (node.children) {
          if (node.type === nodeType.TABLE && node.data?.sid === this.currentNode.f_tableId) {
            isRef = this.compareFbuilder(node);
            return;
          }
        }
      }
    });
    if (isRef) {
      return true;
    }
    return false;
  }

  clearValue(fieldName: string) {
    this.confirmationDialogService.confirm({
      message: this.translate.instant('CLEAR_FORMULA_CONFIRMATION', { fieldName: fieldName }),
      header: 'Confirmation',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.currentParsedFormula = '';
        this.currentActualFormula = '';
        this.formulaArray = [];
        this.selectedFields = [];
        this.savedValue = null;
        this.modelValue = null;
        this.setErrorStatus('none', '');
        this.onChange(this.modelValue);
      },
      reject: () => {
        console.log('cancelled');
      }
    });
  }

  saveFormula(e: any) {
    if (this.formulaArray.length) {
      if (this.validateFormula()) {
        if (!this.isCirRef()) {
          // this.setErrorStatus('none','');
          this.showFbuilder = false;
          this.showForm = false;
          this.commaFormula = false;
          // Removing formControlName since there are parsing issues in JSON deserialization
          const formulaArrayWithoutFormControlName = this.formulaArray.map(({ formControlName, ...obj }) => obj);
          this.modelValue = JSON.stringify(formulaArrayWithoutFormControlName);
          this.currentParsedFormula = this.currentActualFormula;
          this.savedValue = this.modelValue;
          this.onChange(this.modelValue);
          this.setErrorStatus('none', '');
        }
      }
    } else {
      this.errorMsg = this.translate.instant('INVALID_FORMULA');
    }
  }

  backUpFormulaArray: any[] = [];
  setConfiguredFormula(value: string) {
    let fieldTimeout;
    if (!this.allFields) {
      fieldTimeout = setTimeout(() => {
        this.setConfiguredFormula(value);
      }, 200);
      return;
    }
    if (value?.length) {
      this.formulaArray = JSON.parse(value);
      this.backUpFormulaArray = this.formulaArray;
      this.formulaArray.map((fa: any) => {
        const field = this.allFields.filter((af: any) => af.sid === fa.sid);
        if (field.length) {
          fa.fieldId = field[0].fieldId;
        }
      });
      this.constructDisplayFormula(true);
      this.currentParsedFormula = this.currentActualFormula;
    } else {
      this.currentActualFormula = '';
      this.currentParsedFormula = '';
      this.formulaArray = [];
    }
  }

  openFbuilder() {
    this.showFbuilder = true;
    this.selectedFields = [];
    this.errorMsg = '';
    this.selectedField = null;
    this.numberFld = null;
    this.setConfiguredFormula(this.savedValue);
  }

  closeDialog(event) {
    this.currentActualFormula = '';
    this.showFbuilder = false;
    this.showForm = false;
    this.commaFormula = false;
    this.formulaArray = this.backUpFormulaArray;
    this.validateFormula();
  }

  ngOnInit(): void {
    this.setErrorStatus('none', '');
    if (this.currentNode) {
      this.getFields(this.currentNode.f_tableId);
    }
    this.testOutput = null;
  }

  ngOnDestroy(): void {
    this.allFields$?.unsubscribe();
    this.lookupFields$?.unsubscribe();
    if ($(this.elementRef.nativeElement)) {
      $(this.elementRef.nativeElement).remove();
    }
  }

  test() {
    this.testOutput = '';
    const formula = this.formulaArray;
    let expression = '';
    if (formula) {
      formula.forEach((f) => {
        if (f.type !== 'field') {
          expression += f.fieldId;
        } else {
          let _fieldName = this.fieldIdMap[f.fieldId].includes('.')
            ? this.fieldIdMap[f.fieldId].split('.')[1]
            : this.fieldIdMap[f.fieldId];
          _fieldName = _fieldName.replace(/ /g, '');

          if (this.formulaForm.controls[_fieldName.toLowerCase()].value === null) {
            const v = Math.floor(Math.random() * 101);
            this.formulaForm.get(_fieldName.toLowerCase())?.patchValue(v, { emitEvent: false });
          }
          expression += this.formulaForm.controls[_fieldName.toLowerCase()].value;
        }
      });
    }
    try {
      try {
        // Use expression-eval to safely evaluate the expression
        const compiled = compile(expression);
        this.testOutput = compiled({});
        if (this.testOutput === Infinity) {
          this.showMessage('error', 'Invalid Expression.');
          return false;
        }
        return true;
      } catch (err) {
        this.testOutput = 'Invalid Expression';
        this.showMessage('error', 'Invalid Expression.');
        return false;
      }
    } catch (err) {
      this.testOutput = 'Invalid Expression';
      this.showMessage('error', 'Invalid Expression.');
      return false;
    }
  }

  deleteAll() {
    this.confirmationDialogService.confirm({
      message: 'Are you sure you want to clear entire expression?',
      accept: () => {
        this.expressionForm.controls.expression.setValue(null);
        this.formulaArray = [];
        this.selectedFields = [];
        this.testOutput = '';
      },
      reject: () => {
        /* empty */
      }
    });
  }

  showMessage(status: string, message: string, detail?: string) {
    this.messageService.clear();
    this.messageService.add({
      life: 10000,
      severity: status,
      summary: message,
      detail: detail
    });
  }

  addOperator(operator) {
    setTimeout(() => {
      $('.p-inputchips-input-item input').attr('placeholder', 'Enter Number');
    }, 100);
    const value = this.expressionForm.controls.expression.value || [];
    value.push({ type: 'operator', fieldId: operator, value: operator, sid: uuidv4() });
    this.expressionForm.controls.expression.setValue(value);
  }

  addPlaceHolder($event) {
    setTimeout(() => {
      $('.p-inputchips-input-item input').attr('placeholder', 'Enter Number');
    }, 100);
    if ($event.value && typeof $event.value === 'string') {
      const val = Number($event.value);
      const value = this.expressionForm.controls.expression.value;
      value.pop();
      if (Number.isNaN(val)) {
        this.showMessage('error', 'Only numbers allowed.');
      } else {
        value.push({ type: 'number', fieldId: val, value: val, sid: uuidv4() });
      }
      this.expressionForm.controls.expression.setValue(value);
    }
    return true;
  }

  removeExpression($event) {
    if ($event.value.type === 'field') {
      const values = this.expressionForm.controls.expression.value;
      const val = values.filter((v: any) => v.fieldId === $event.value.fieldId);
      if (val.length === 0) {
        this.selectedFields = this.selectedFields.filter((f: any) => f.fieldId !== $event.value.fieldId);
      }
    }
  }
}




